import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getConfigEditorBridgeState, subscribeConfigEditorBridge } from 'config-editor-common';
import type { AiPlan, PlanStep } from '../types';
import {
  findAnchor,
  parseLambdaAnchor,
  resolveAnchorElement,
  revealAnchor,
  revealResolvedAnchor,
} from './anchors';

export type DriverStatus = 'idle' | 'running' | 'awaiting-user' | 'done' | 'error';

export interface AiDriverState {
  status: DriverStatus;
  /** Index into plan.steps of the step currently shown, or -1 when idle. */
  activeStepIndex: number;
  currentStep: PlanStep | null;
  /** Anchor the coach-mark should point at (null for navigate / idle). */
  currentAnchor: string | null;
  totalSteps: number;
  error: string | null;
}

export interface AiDriver extends AiDriverState {
  run: (plan: AiPlan) => void;
  next: () => void;
  cancel: () => void;
}

/** Soft cap: after a navigate, wait at most this long for the editor to become ready. */
const READY_TIMEOUT_MS = 15000;
/** How long to wait for an accordion/tab reveal to render a target anchor. */
const RENDER_TIMEOUT_MS = 3000;
/** Let the config-editor's debounced dirty-diff catch up after a state-based setField. */
const SETTLE_MS = 250;
/** Anchor that signals the scaffolder wizard has mounted (create-drive navigate target). */
const SCAFFOLDER_READY_ANCHOR = 'scaffolder.wizard';

/** config-editor routes drive via the shared bridge; every other route (scaffolder) does not. */
function isConfigEditorUrl(url: string): boolean {
  // Match the exact route (query string aside), not a `/config-editor…` prefix.
  return url.split(/[?#]/)[0] === '/config-editor';
}

const IDLE: AiDriverState = {
  status: 'idle',
  activeStepIndex: -1,
  currentStep: null,
  currentAnchor: null,
  totalSteps: 0,
  error: null,
};

const sleep = (ms: number) => new Promise<void>(r => setTimeout(r, ms));

interface Target {
  type?: string;
  component?: string;
  item?: string;
  branch?: string;
}

export function parseTarget(url: string): Target {
  const q = url.includes('?') ? url.slice(url.indexOf('?') + 1) : '';
  const params = new URLSearchParams(q);
  return {
    type: params.get('type') ?? undefined,
    component: params.get('component') ?? undefined,
    item: params.get('item') ?? undefined,
    branch: params.get('branch') ?? undefined,
  };
}

/**
 * The editor is "ready for this target" only when its published selection matches every identifier the
 * navigate URL carried (component + item required; type/branch checked when present). A target with no
 * component/item is treated as NOT a match — we never want a setField landing on "whatever config
 * happens to be open".
 */
export function selectionMatches(
  selection: ReturnType<typeof getConfigEditorBridgeState>['selection'],
  target: Target,
): boolean {
  if (!target.component || !target.item) return false;
  if (!selection) return false;
  if (selection.component !== target.component) return false;
  if (selection.item !== target.item) return false;
  if (target.type && selection.type !== target.type) return false;
  if (target.branch && selection.branch !== target.branch) return false;
  return true;
}

/**
 * The AI edit-drive engine. Executes a plan's steps one at a time against the real config-editor UI,
 * narrating each with a coach-mark, and STOPS at the terminal step (Save) for the human to click.
 * Field edits go through the config-editor bridge (state-based) rather than DOM simulation; expand /
 * highlight / navigate use data-ai-anchor DOM nodes + the router.
 */
export function useAiDriver(): AiDriver {
  const navigate = useNavigate();
  const [state, setState] = useState<AiDriverState>(IDLE);

  const planRef = useRef<AiPlan | null>(null);
  const busyRef = useRef(false);
  const mountedRef = useRef(true);
  // Monotonic run generation. Bumped by run() and cancel() so an in-flight async step chain from a
  // cancelled / superseded plan becomes a no-op (checked after every await before any side effect).
  const genRef = useRef(0);
  // Mirror of `state` for imperative reads (next/cancel) without stale closures.
  const stateRef = useRef<AiDriverState>(IDLE);
  // Teardown for an in-flight waitForReady() so cancel()/run() can drop its bridge subscription and
  // timer immediately, instead of leaking them until ready/timeout.
  const pendingWaitCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const commit = useCallback((nextState: AiDriverState) => {
    stateRef.current = nextState;
    if (mountedRef.current) setState(nextState);
  }, []);

  const setSafe = useCallback(
    (patch: Partial<AiDriverState>) => {
      commit({ ...stateRef.current, ...patch });
    },
    [commit],
  );

  // Resolve once the editor is ready for the navigated target (or after a soft timeout).
  const waitForReady = useCallback((url: string) => {
    const target = parseTarget(url);
    const ready = () => {
      const { edit, selection } = getConfigEditorBridgeState();
      return !!edit?.ready && selectionMatches(selection, target);
    };
    return new Promise<void>(resolve => {
      if (ready()) {
        resolve();
        return;
      }
      let done = false;
      const cleanup: {
        unsub?: () => void;
        timer?: ReturnType<typeof setTimeout>;
      } = {};
      const finish = () => {
        if (done) return;
        done = true;
        cleanup.unsub?.();
        if (cleanup.timer) clearTimeout(cleanup.timer);
        if (pendingWaitCleanupRef.current === finish) {
          pendingWaitCleanupRef.current = null;
        }
        resolve();
      };
      pendingWaitCleanupRef.current = finish;
      cleanup.unsub = subscribeConfigEditorBridge(() => {
        if (ready()) finish();
      });
      cleanup.timer = setTimeout(finish, READY_TIMEOUT_MS);
    });
  }, []);

  const ensureRendered = useCallback(
    async (anchor: string, timeoutMs: number = RENDER_TIMEOUT_MS, gen?: number) => {
      const deadline = Date.now() + timeoutMs;
      while (Date.now() < deadline) {
        // Bail if a cancel()/new run superseded this generation while polling (up to 15s for the
        // scaffolder), instead of blocking the poll to its full deadline.
        if (gen !== undefined && gen !== genRef.current) return false;
        // Same resolution the coach-mark uses: a lambda-scoped anchor whose element is missing still
        // counts as rendered once its settings fallback exists — otherwise the drive would poll the
        // full timeout for an anchor this template never renders, then point at nothing.
        if (resolveAnchorElement(anchor)) return true;
        // eslint-disable-next-line no-await-in-loop
        await sleep(100);
      }
      return false;
    },
    [],
  );

  // True while `gen` is still the active run. Any step whose generation is stale (cancel/new run
  // happened during an await) must abort BEFORE its side effects, so a cancelled plan never mutates
  // config, clicks accordions, or resurrects a non-idle state.
  const isCurrent = useCallback((gen: number) => gen === genRef.current, []);

  // Reveal a lambda's settings section. The config-editor renders endpoint vs authorizer lambdas in
  // separate tabs, so the target may not be in the DOM until the right tab is active — click through
  // the lambda tabs until the section renders, then expand + scroll to it.
  const revealLambdaSettings = useCallback(
    async (name: string, gen: number) => {
      const settingsAnchor = `lambda.${name}.settings`;
      if (!findAnchor(settingsAnchor)) {
        const tabs = ['lambda-tab.endpoint', 'lambda-tab.authorizer'];
        for (const tab of tabs) {
          if (!isCurrent(gen)) return false;
          const tabEl = findAnchor(tab);
          if (!tabEl) continue;
          tabEl.click();
          // Tab content mounts fast; a short probe is enough to tell if this was the right tab.
          // eslint-disable-next-line no-await-in-loop
          if (await ensureRendered(settingsAnchor, 800, gen)) break;
        }
      }
      const found = await ensureRendered(settingsAnchor, READY_TIMEOUT_MS, gen);
      if (found && isCurrent(gen)) revealAnchor(settingsAnchor);
      return found;
    },
    [ensureRendered, isCurrent],
  );

  const applyStep = useCallback(
    async (step: PlanStep, gen: number) => {
      switch (step.action) {
        case 'navigate': {
          if (step.url) {
            navigate(step.url);
            const path = step.url.split(/[?#]/)[0];
            if (isConfigEditorUrl(step.url)) {
              await waitForReady(step.url);
            } else if (path.startsWith('/create/')) {
              // Scaffolder (create): wait for the wizard footer to mount so the subsequent highlight
              // steps can target the stepper buttons. No config-editor bridge here.
              await ensureRendered(SCAFFOLDER_READY_ANCHOR, READY_TIMEOUT_MS, gen);
            } else {
              // Any other in-app route (e.g. the API's catalog page): just let it render — the next
              // step (a click on that page's control) polls for its own anchor.
              await sleep(SETTLE_MS);
            }
          }
          break;
        }
        case 'expand': {
          if (step.anchor) {
            const parsed = parseLambdaAnchor(step.anchor);
            if (parsed) {
              // Lambda section: switch tab if needed, then expand + scroll.
              await revealLambdaSettings(parsed.name, gen);
            } else {
              await ensureRendered(step.anchor, RENDER_TIMEOUT_MS, gen);
              if (!isCurrent(gen)) return;
              revealAnchor(step.anchor);
            }
          }
          break;
        }
        case 'setField': {
          const parsed = step.anchor ? parseLambdaAnchor(step.anchor) : null;
          // Reveal the lambda's settings (switching tab if the lambda is on the other one) so the
          // changed field is actually visible to the user.
          if (parsed) {
            await revealLambdaSettings(parsed.name, gen);
          }
          if (!isCurrent(gen)) return;
          const { edit } = getConfigEditorBridgeState();
          if (!edit) {
            throw new Error('config-editor is not open — navigate first');
          }
          if (!parsed || step.field === undefined) {
            throw new Error('setField step is missing a lambda anchor or field');
          }
          const result = edit.setLambdaField(parsed.name, step.field, step.value);
          if (!result.ok) {
            throw new Error(result.error ?? `could not set ${step.field}`);
          }
          // Let the dirty-diff debounce run so Save lights up, then point at the field.
          await sleep(SETTLE_MS);
          if (!isCurrent(gen)) return;
          if (step.anchor) revealAnchor(step.anchor);
          break;
        }
        case 'click': {
          // Click a real navigation control (e.g. the "Open in Config Editor" button on the API's
          // catalog page) so the user sees the app move as they would by hand. Non-terminal — the
          // terminal Save/Create is still only highlighted, never clicked.
          if (step.anchor) {
            const found = await ensureRendered(step.anchor, READY_TIMEOUT_MS, gen);
            if (!isCurrent(gen)) return;
            const el = found ? revealAnchor(step.anchor) : null;
            if (!el) {
              throw new Error(`could not find "${step.anchor}" to click`);
            }
            el.click();
            // Let the resulting navigation / render settle before the next step polls for its anchor.
            await sleep(SETTLE_MS);
          }
          break;
        }
        case 'addLambda': {
          const { edit } = getConfigEditorBridgeState();
          if (!edit) {
            throw new Error('config-editor is not open — navigate first');
          }
          if (!edit.addLambda) {
            throw new Error('this editor does not support adding lambdas');
          }
          if (!step.name) {
            throw new Error('addLambda step is missing a name');
          }
          const group = step.group === 'authorizer' ? 'authorizer' : 'endpoint';
          const result = edit.addLambda(group, step.name);
          if (!result.ok) {
            throw new Error(result.error ?? `could not add lambda "${step.name}"`);
          }
          // Let the dirty-diff debounce run so Save lights up, then reveal the new lambda's section so
          // the user sees it and the follow-up setField steps can target it.
          await sleep(SETTLE_MS);
          if (!isCurrent(gen)) return;
          await revealLambdaSettings(step.name, gen);
          break;
        }
        case 'highlight': {
          if (step.anchor) {
            // A lambda-scoped anchor (a field or the delete button) may live on the inactive
            // endpoint/authorizer tab — reveal the lambda first so the anchor can mount, instead of
            // requiring the plan to carry a separate expand step.
            const parsed = parseLambdaAnchor(step.anchor);
            if (parsed && !findAnchor(step.anchor)) {
              await revealLambdaSettings(parsed.name, gen);
            }
            await ensureRendered(step.anchor, RENDER_TIMEOUT_MS, gen);
            if (!isCurrent(gen)) return;
            // Reveal with the coach-mark's fallback resolution so the scroll/expand lands on the
            // same element the overlay points at (settings section when the exact anchor is absent).
            const el = revealResolvedAnchor(step.anchor);
            // Terminal step: draw the eye to Save and focus it, but NEVER click it. Never focus a
            // delete control either — focus would arm it for the user's next Enter/Space keystroke.
            // validatePlan already restricts terminal anchors to submit controls; defense in depth.
            if (step.terminal && el && parseLambdaAnchor(step.anchor)?.part !== 'delete') {
              el.focus?.();
            }
          }
          break;
        }
        default:
          break;
      }
    },
    [navigate, waitForReady, ensureRendered, isCurrent, revealLambdaSettings],
  );

  const executeStep = useCallback(
    async (index: number, gen: number) => {
      if (!isCurrent(gen)) return;
      const steps = planRef.current?.steps ?? [];
      const step = steps[index];
      if (!step) {
        setSafe({ status: 'done', currentAnchor: null });
        return;
      }
      busyRef.current = true;
      setSafe({
        status: 'running',
        activeStepIndex: index,
        currentStep: step,
        currentAnchor: null,
        error: null,
      });
      try {
        await applyStep(step, gen);
      } catch (e: any) {
        if (!isCurrent(gen)) return;
        busyRef.current = false;
        setSafe({ status: 'error', error: e?.message ?? String(e) });
        return;
      }
      // Cancelled / superseded while the step was running — leave the fresh state alone.
      if (!isCurrent(gen)) return;
      busyRef.current = false;
      setSafe({
        status: step.terminal ? 'done' : 'awaiting-user',
        currentAnchor: step.anchor ?? null,
      });
    },
    [applyStep, setSafe, isCurrent],
  );

  const run = useCallback(
    (plan: AiPlan) => {
      planRef.current = plan;
      // Invalidate any still-in-flight chain from a previous plan and tear down its pending wait.
      pendingWaitCleanupRef.current?.();
      const gen = ++genRef.current;
      busyRef.current = false;
      const steps = plan.steps ?? [];
      // Clarify-only / summary-only plans have nothing to drive.
      commit({ ...IDLE, totalSteps: steps.length });
      if (steps.length === 0) return;
      void executeStep(0, gen);
    },
    [commit, executeStep],
  );

  const next = useCallback(() => {
    if (busyRef.current) return;
    if (stateRef.current.status !== 'awaiting-user') return;
    void executeStep(stateRef.current.activeStepIndex + 1, genRef.current);
  }, [executeStep]);

  const cancel = useCallback(() => {
    planRef.current = null;
    busyRef.current = false;
    // Invalidate the in-flight chain so its post-await side effects become no-ops, and tear down any
    // pending waitForReady subscription/timer immediately.
    genRef.current += 1;
    pendingWaitCleanupRef.current?.();
    commit(IDLE);
  }, [commit]);

  return useMemo(() => ({ ...state, run, next, cancel }), [state, run, next, cancel]);
}
